<template>
  <div class="equipment-account-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('equipmentAccount.page.title')" :subtitle="$t('equipmentAccount.page.subtitle')">
      <template #extra>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openAddEquipmentAccountModal">
          {{ $t('equipmentAccount.addEquipment') }}
        </el-button>
        <el-button size="small" icon="el-icon-printer" @click="printEquipmentAccounts">
          {{ $t('equipmentAccount.batchPrint') }}
        </el-button>
      </template>
    </ds-page-header>

    <el-row :gutter="16" class="margin-top">
      <el-col :xs="24" :sm="6" :md="5" :lg="4">
        <div class="ds-panel ds-panel--padded">
          <machine-type-tree ref="machineTypeTree" @switchType="handleSwitchType" />
        </div>
      </el-col>
      <el-col :xs="24" :sm="18" :md="19" :lg="20">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="6" :lg="6">
              <el-input v-model.trim="equipmentAccountInfo.conditions.machineName" clearable class="w-full"
                :placeholder="$t('equipmentAccount.machineNamePlaceholder')" @keyup.enter.native="queryEquipmentAccountMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="6" :lg="6">
              <el-input v-model.trim="equipmentAccountInfo.conditions.machineCode" clearable class="w-full"
                :placeholder="$t('equipmentAccount.machineCodePlaceholder')" @keyup.enter.native="queryEquipmentAccountMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="6" :lg="6">
              <el-select v-model="equipmentAccountInfo.conditions.state" clearable class="w-full"
                :placeholder="$t('equipmentAccount.selectState')">
                <el-option :label="$t('equipmentAccount.selectState')" value="" />
                <el-option v-for="item in equipmentAccountInfo.useStatus" :key="item.statusCd" :label="item.name"
                  :value="item.statusCd" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="6" :lg="6">
              <el-select v-model="equipmentAccountInfo.conditions.importanceLevel" clearable class="w-full"
                :placeholder="$t('equipmentAccount.selectImportanceLevel')">
                <el-option :label="$t('equipmentAccount.selectImportanceLevel')" value="" />
                <el-option v-for="item in equipmentAccountInfo.importanceLevels" :key="item.statusCd" :label="item.name"
                  :value="item.statusCd" />
              </el-select>
            </el-col>
          </el-row>
          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="queryEquipmentAccountMethod">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="resetEquipmentAccountMethod">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="equipmentAccountInfo.total" :density="density" :show-column-setting="false"
            :show-refresh="true" @update:density="density = $event" @refresh="listEquipmentAccounts">
            <template #left>
              <span class="ds-panel__title">{{ $t('equipmentAccount.page.listTitle') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="equipmentAccountInfo.equipmentAccounts" :loading="loading" :error="hasError"
            :error-message="errorMessage" :density="density" border :empty-description="$t('equipmentAccount.page.empty')"
            @retry="listEquipmentAccounts" @selection-change="handleSelectionChange">
            <el-table-column type="selection" width="55" align="center" />
            <el-table-column prop="machineName" :label="$t('equipmentAccount.machineName')" align="center" min-width="140" />
            <el-table-column prop="machineCode" :label="$t('equipmentAccount.machineCode')" align="center" width="140">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.machineCode || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="brand" :label="$t('equipmentAccount.brand')" align="center" width="100" />
            <el-table-column prop="model" :label="$t('equipmentAccount.model')" align="center" width="100" />
            <el-table-column prop="locationDetail" :label="$t('equipmentAccount.locationDetail')" align="center" min-width="140" />
            <el-table-column prop="firstEnableTime" :label="$t('equipmentAccount.firstEnableTime')" align="center" width="150">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.firstEnableTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="warrantyDeadline" :label="$t('equipmentAccount.warrantyDeadline')" align="center" width="150">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.warrantyDeadline || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="usefulLife" :label="$t('equipmentAccount.usefulLife')" align="center" width="100" />
            <el-table-column prop="levelName" :label="$t('equipmentAccount.importanceLevel')" align="center" width="110" />
            <el-table-column prop="stateName" :label="$t('equipmentAccount.state')" align="center" width="100">
              <template slot-scope="scope">
                <ds-status-tag size="sm" type="info" :label="scope.row.stateName || '-'" :text="scope.row.stateName" />
              </template>
            </el-table-column>
            <el-table-column prop="purchasePrice" :label="$t('equipmentAccount.purchasePrice')" align="right" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatMoney(scope.row.purchasePrice) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="netWorth" :label="$t('equipmentAccount.netWorth')" align="right" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatMoney(scope.row.netWorth) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="240" fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" @click="printEquipmentAccount(scope.row)">{{ $t('common.print') }}</el-button>
                <el-button size="mini" @click="openEquipmentAccountDetail(scope.row)">{{ $t('common.detail') }}</el-button>
                <el-button size="mini" @click="openEditEquipmentAccountModel(scope.row)">{{ $t('common.edit') }}</el-button>
                <el-button size="mini" type="danger" @click="openDeleteEquipmentAccountModel(scope.row)">{{ $t('common.delete') }}</el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :page="equipmentAccountInfo.currentPage" :size="pageSize"
            :total="equipmentAccountInfo.total" :page-sizes="[10, 20, 30, 50]"
            :summary="$t('equipmentAccount.stateDescription')"
            @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </el-col>
    </el-row>

    <!-- 子组件 -->
    <delete-equipment-account ref="deleteEquipmentAccount" @success="listEquipmentAccounts" />
    <view-equipment-account ref="viewEquipmentAccount" />
    <print-equipment-account ref="printEquipmentAccount" />
    <import-equipment ref="importEquipment" />
  </div>
</template>

<script>
import { getDict } from '@/api/community/communityApi'
import { getCommunityId } from '@/api/community/communityApi'
import { listEquipmentAccount } from '@/api/machine/equipmentAccountApi'
import MachineTypeTree from '@/components/machine/machineTypeTree'
import DeleteEquipmentAccount from '@/components/machine/deleteEquipmentAccount'
import ViewEquipmentAccount from '@/components/machine/viewEquipmentAccount'
import PrintEquipmentAccount from '@/components/machine/printEquipmentAccount'
import ImportEquipment from '@/components/machine/importEquipment'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'EquipmentAccountList',
  components: {
    MachineTypeTree,
    DeleteEquipmentAccount,
    ViewEquipmentAccount,
    PrintEquipmentAccount,
    ImportEquipment,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      pageSize: 10,
      communityId: '',
      equipmentAccountInfo: {
        currentPage: 1,
        equipmentAccounts: [],
        total: 0,
        moreCondition: false,
        machineIds: [],
        conditions: {
          machineName: '',
          machineCode: '',
          state: '',
          importanceLevel: '',
          typeId: ''
        },
        useStatus: [],
        importanceLevels: []
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.getDictData()
    this.listEquipmentAccounts()
  },
  methods: {
    formatMoney(v) { return formatMoney(v) },
    async getDictData() {
      try {
        const stateData = await getDict('equipment_account', 'state')
        const levelData = await getDict('equipment_account', 'importance_level')
        this.equipmentAccountInfo.useStatus = stateData
        this.equipmentAccountInfo.importanceLevels = levelData
      } catch (error) {
        console.error('获取字典数据失败:', error)
      }
    },
    handleSelectionChange(selectedRows) {
      this.equipmentAccountInfo.machineIds = selectedRows.map(row => row.machineId)
    },
    async listEquipmentAccounts() {
      this.loading = true
      this.hasError = false
      try {
        const params = {
          page: this.equipmentAccountInfo.currentPage,
          row: this.pageSize,
          communityId: this.communityId,
          ...this.equipmentAccountInfo.conditions
        }
        const { data, total } = await listEquipmentAccount(params)
        this.equipmentAccountInfo.equipmentAccounts = data
        this.equipmentAccountInfo.total = total
      } catch (error) {
        console.error('获取设备台账列表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('common.loadError')
      } finally {
        this.loading = false
      }
    },
    handleSwitchType(type) {
      this.equipmentAccountInfo.conditions.typeId = type.typeId
      this.listEquipmentAccounts()
    },
    openAddEquipmentAccountModal() {
      if (!this.equipmentAccountInfo.conditions.typeId) {
        this.$message.warning(this.$t('equipmentAccount.selectTypeFirst'))
        return
      }
      this.$router.push(`/views/machine/addEquipmentAccount?typeId=${this.equipmentAccountInfo.conditions.typeId}`)
    },
    openEditEquipmentAccountModel(row) {
      this.$router.push(`/views/machine/editEquipmentAccount?machineId=${row.machineId}`)
    },
    openEquipmentAccountDetail(row) {
      this.$router.push(`/views/machine/equipmentAccountDetail?machineId=${row.machineId}`)
    },
    openDeleteEquipmentAccountModel(row) {
      this.$refs.deleteEquipmentAccount.open(row)
    },
    printEquipmentAccount(row) {
      window.open(`/#/views/resource/printEquipmentAccountLabel?machineId=${row.machineId}`)
    },
    printEquipmentAccounts() {
      if (this.equipmentAccountInfo.machineIds.length === 0) {
        this.$message.warning(this.$t('equipmentAccount.selectEquipmentFirst'))
        return
      }
      window.open(`/#/views/resource/printEquipmentAccountLabel?machineIds=${this.equipmentAccountInfo.machineIds}`)
    },
    queryEquipmentAccountMethod() {
      this.equipmentAccountInfo.currentPage = 1
      this.listEquipmentAccounts()
    },
    resetEquipmentAccountMethod() {
      this.equipmentAccountInfo.conditions = {
        machineName: '',
        machineCode: '',
        state: '',
        importanceLevel: '',
        typeId: this.equipmentAccountInfo.conditions.typeId
      }
      this.listEquipmentAccounts()
    },
    toggleMoreCondition() {
      this.equipmentAccountInfo.moreCondition = !this.equipmentAccountInfo.moreCondition
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.equipmentAccountInfo.currentPage = 1
      this.listEquipmentAccounts()
    },
    handleCurrentChange(val) {
      this.equipmentAccountInfo.currentPage = val
      this.listEquipmentAccounts()
    }
  }
}
</script>

<style lang="scss" scoped>
.equipment-account-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &--padded {
    padding: var(--ds-spacing-lg);
  }

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.margin-top {
  margin-top: 0;
}

.w-full {
  width: 100%;
}
</style>
